/**
 * Cross-browser compatible clipboard utilities
 * Supports modern and legacy browsers, including mobile devices
 */

/**
 * Copy text to clipboard with fallback support for all devices
 * @param text - Text to copy to clipboard
 * @returns Promise<boolean> - true if successful, false otherwise
 */
export const copyToClipboard = async (text: string): Promise<boolean> => {
    // Try modern Clipboard API first (works on most modern browsers)
    if (navigator.clipboard && navigator.clipboard.writeText) {
        try {
            await navigator.clipboard.writeText(text)
            return true
        } catch (err) {
            console.error('Modern Clipboard API failed:', err)
            // Fall through to legacy method
        }
    }

    // Fallback to legacy execCommand method (works on older browsers and some mobile devices)
    return copyToClipboardLegacy(text)
}

/**
 * Legacy clipboard copy using execCommand
 * This is the fallback method for older browsers and certain mobile devices
 * @param text - Text to copy to clipboard
 * @returns boolean - true if successful, false otherwise
 */
const copyToClipboardLegacy = (text: string): boolean => {
    try {
        // Create a temporary textarea element
        const textArea = document.createElement('textarea')
        
        // Set styles to hide the element
        textArea.style.position = 'fixed'
        textArea.style.left = '-999999px'
        textArea.style.top = '-999999px'
        textArea.style.width = '2em'
        textArea.style.height = '2em'
        textArea.style.padding = '0'
        textArea.style.border = 'none'
        textArea.style.outline = 'none'
        textArea.style.boxShadow = 'none'
        textArea.style.background = 'transparent'
        textArea.style.opacity = '0'
        textArea.style.pointerEvents = 'none'
        
        // Set the text content
        textArea.value = text
        
        // Append to body
        document.body.appendChild(textArea)
        
        // Select the text
        textArea.focus()
        textArea.select()
        
        // Try to copy
        const successful = document.execCommand('copy')
        
        // Remove the temporary element
        document.body.removeChild(textArea)
        
        return successful
    } catch (err) {
        console.error('Legacy clipboard copy failed:', err)
        return false
    }
}

/**
 * Copy text and optionally validate with a test read
 * More thorough but slightly slower
 * @param text - Text to copy to clipboard
 * @returns Promise<boolean> - true if successful
 */
export const copyToClipboardWithVerification = async (text: string): Promise<boolean> => {
    try {
        // Try modern API with verification
        if (navigator.clipboard && navigator.clipboard.writeText) {
            await navigator.clipboard.writeText(text)
            
            // Verify by reading back (if permissions allow)
            try {
                const clipboardText = await navigator.clipboard.readText()
                return clipboardText === text
            } catch {
                // Read might fail due to permissions, but write likely succeeded
                return true
            }
        }
    } catch (err) {
        console.error('Modern Clipboard API failed:', err)
    }

    // Fall back to legacy method
    return copyToClipboardLegacy(text)
}

/**
 * Mobile-friendly clipboard copy with better error handling
 * Use this for mobile-specific scenarios
 * @param text - Text to copy to clipboard
 * @returns Promise<boolean> - true if successful, false otherwise
 */
export const copyToClipboardMobile = async (text: string): Promise<boolean> => {
    // On mobile, try legacy method first (often more reliable)
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
    
    if (isMobile) {
        // Try legacy first on mobile
        if (copyToClipboardLegacy(text)) {
            return true
        }
        // Then try modern API
        return copyToClipboard(text)
    }
    
    // On desktop, try modern first
    return copyToClipboard(text)
}